import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../components/EmptyState';
import InlineAlert from '../../components/InlineAlert';
import { useBilling } from '../../context/useBilling';

const CurrentBill = () => {
  const navigate = useNavigate();
  const { account, currentBill, loadCurrentBill, loading, error } = useBilling();
  const handlePayNow = () => navigate('../collect-payment');

  useEffect(() => {
    if (account && !currentBill && !loading.bill) {
      loadCurrentBill();
    }
  }, [account, currentBill, loadCurrentBill, loading.bill]);

  if (!account) {
    return (
      <EmptyState
        icon="receipt"
        eyebrow="Current Bill"
        title="No Customer Selected"
        message="Select or look up a customer account first to review the current bill summary."
        compact
        actions={(
          <button className="btn btn-outline-tse-primary" onClick={() => navigate('../customer-lookup')}>
            <i className="bi bi-search me-2"></i>Find Customer
          </button>
        )}
      />
    );
  }

  if (loading.bill && !currentBill) {
    return (
      <div className="d-flex align-items-center gap-2">
        <span className="spinner-border" role="status" aria-hidden="true"></span>
        <span>Loading current bill...</span>
      </div>
    );
  }

  if (error && !currentBill) {
    return <InlineAlert variant="danger" icon="exclamation-octagon" ariaLabel="Current bill error" message={error} />;
  }

  if (!currentBill) {
    return null;
  }

  const amountDue = currentBill.totals.amountDue.toFixed(2);
  const daysUntilDue = Math.ceil((new Date(currentBill.period.dueDate) - Date.now()) / 86400000);

  const dueBadgeText = daysUntilDue > 0
    ? `Due in ${daysUntilDue} days`
    : daysUntilDue === 0
      ? 'Due Today'
      : `${Math.abs(daysUntilDue)} days overdue`;

  return (
    <div className="row mb-4 billing-panel" role="tabpanel" id="billing-panel-current-bill" aria-labelledby="billing-tab-current-bill" tabIndex={0} data-testid="billing-panel-current-bill">
      <div className="col-12">
        <div className="card border-0 shadow-sm billing-surface-card billing-surface-card--feature">
          <div className="card-header bg-tse-primary text-white d-flex justify-content-between align-items-center billing-surface-card__feature-header">
            <h5 className="mb-0"><i className="bi bi-receipt me-2"></i>Current Bill Summary</h5>
            <span id="current-bill-due-badge" className="badge bg-warning text-dark">{dueBadgeText}</span>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-8">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <h6 id="heading-account-info" className="text-muted mb-2">Account Information</h6>
                    <p className="mb-1">
                      <strong id="lbl-account-number">Account #:</strong>{' '}
                      <span id="val-account-number" className="billing-mono" aria-labelledby="lbl-account-number">{account.accountNumber}</span>
                    </p>
                    <p className="mb-1"><strong id="lbl-service-address">Service Address:</strong></p>
                    <p id="val-service-street" className="mb-1" aria-labelledby="lbl-service-address">{account.serviceAddress.street}</p>
                    <p id="val-service-city-state-zip" className="mb-3" aria-labelledby="lbl-service-address">
                      {account.serviceAddress.city}, {account.serviceAddress.state} {account.serviceAddress.postalCode}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <h6 id="heading-billing-period" className="text-muted mb-2">Billing Period</h6>
                    <p className="mb-1">
                      <strong id="lbl-billing-from">From:</strong>{' '}
                      <span id="val-billing-from" aria-labelledby="lbl-billing-from">{new Date(currentBill.period.start).toLocaleDateString()}</span>
                    </p>
                    <p className="mb-1">
                      <strong id="lbl-billing-to">To:</strong>{' '}
                      <span id="val-billing-to" aria-labelledby="lbl-billing-to">{new Date(currentBill.period.end).toLocaleDateString()}</span>
                    </p>
                    <p className="mb-1">
                      <strong id="lbl-billing-days">Days:</strong>{' '}
                      <span id="val-billing-days" aria-labelledby="lbl-billing-days">{currentBill.period.days}</span>
                    </p>
                    <p className="mb-1">
                      <strong id="lbl-due-date">Due Date:</strong>{' '}
                      <span id="val-due-date" className="text-danger fw-bold" aria-labelledby="lbl-due-date">{new Date(currentBill.period.dueDate).toLocaleDateString()}</span>
                    </p>
                  </div>
                </div>
                <div className="table-responsive billing-table-wrap" role="region" aria-label="Current bill line items">
                  <table className="table table-sm billing-table" data-testid="table-current-bill">
                    <caption className="visually-hidden">Line items and total amount due for current billing period</caption>
                    <thead className="table-light">
                      <tr>
                        <th scope="col" id="col-charge-description">Charge Description</th>
                        <th scope="col" id="col-charge-amount" className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentBill.lineItems.map(li => (
                        <tr key={li.code} id={`current-bill-line-${li.code}`}>
                          <td id={`current-bill-line-desc-${li.code}`} headers="col-charge-description">{li.description}</td>
                          <td id={`current-bill-line-amount-${li.code}`} headers="col-charge-amount" className={`text-end ${li.amount < 0 ? 'text-success' : ''}`}>
                            {(li.amount < 0 ? '-' : '')}${Math.abs(li.amount).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      <tr id="current-bill-row-total" className="table-warning fw-bold" data-testid="bill-total-row">
                        <td headers="col-charge-description"><strong>Total Amount Due</strong></td>
                        <td id="current-bill-total-amount" headers="col-charge-amount" className="text-end" aria-live="polite"><strong>${amountDue}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6">
                    {currentBill.totals.amountDue > 0 ? (
                      <button id="btn-pay-now" className="btn btn-success btn-lg w-100" onClick={handlePayNow}>
                        <i className="bi bi-credit-card me-2"></i>Pay Now - ${amountDue}
                      </button>
                    ) : (
                      <InlineAlert variant="success" icon="check-circle" message="Bill Paid" className="mb-0" />
                    )}
                  </div>
                  <div className="col-md-6">
                    <button id="btn-download-pdf" className="btn btn-outline-primary btn-lg w-100">
                      <i className="bi bi-download me-2"></i>Download PDF
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card bg-light h-100 billing-surface-card billing-surface-card--aside">
                  <div className="card-header bg-transparent">
                    <h6 className="mb-0">Usage Summary</h6>
                  </div>
                  <div className="card-body text-center">
                    <div className="mb-4">
                      <div id="val-usage-kwh" className="circle-chart bg-tse-primary text-white border-0 mb-2" aria-label={`${currentBill.usage.kwh} kilowatt hours used`}>{currentBill.usage.kwh}</div>
                      <div className="stat-title fw-bold">kWh Used</div>
                      <small id="val-usage-kwh-change" className="text-success">{Math.abs(currentBill.usage.kwhChangePct)}% {currentBill.usage.kwhChangePct < 0 ? 'less' : 'more'} than last month</small>
                    </div>
                    <div className="mb-4">
                      <div id="val-usage-temp" className="circle-chart bg-info text-white border-0 mb-2" aria-label={`${currentBill.usage.avgTempF} degrees Fahrenheit average temperature`}>{currentBill.usage.avgTempF}°F</div>
                      <div className="stat-title fw-bold">Avg Temperature</div>
                      <small className="text-muted">This billing period</small>
                    </div>
                    <div>
                      <div id="val-usage-daily-avg" className="circle-chart bg-tse-green text-white border-0 mb-2" aria-label={`$${currentBill.usage.dailyAvgCost} daily average energy cost`}>${currentBill.usage.dailyAvgCost}</div>
                      <div className="stat-title fw-bold">Daily Average</div>
                      <small className="text-muted">Energy cost per day</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentBill;
